import {
  takeLatest,
  put,
  call,
  select,
  take,
  takeEvery
} from 'redux-saga/effects';
import { push } from 'connected-react-router';
import {
  getTemplatesIfNeeded,
  getPaginatedTemplatesIfNeeded,
  getMinimalTemplatesIfNeeded,
  getTemplateNamesByUuidsIfNeeded,
  getTagsIfNeeded,
  getTemplateIfNeeded,
  createTemplateFromCurrent,
  updateTemplateFromCurrent,
  deleteTemplatesByIdList,
  testTemplate,
  warnAndArchive,
  autoSaveTimerStart
} from './sagas';
import {
  GET_TEMPLATES,
  GET_PAGINATED_TEMPLATES,
  GET_MINIMAL_TEMPLATES,
  GET_TEMPLATE_NAMES_BY_UUIDS,
  GET_TEMPLATE,
  CREATE_TEMPLATE,
  CREATING_TEMPLATE_SUCCEEDED,
  UPDATE_TEMPLATE,
  UPDATING_TEMPLATE_SUCCEEDED,
  DELETE_TEMPLATES,
  WARN_AND_ARCHIVE_TEMPLATE,
  getPaginatedTemplates,
  CREATING_TEMPLATE_FAILED,
  UPDATING_TEMPLATE_FAILED,
  GETTING_TEMPLATE_FAILED,
  GETTING_TEMPLATES_FAILED,
  DELETING_TEMPLATES_FAILED,
  GETTING_ASSOCIATED_CAMPAIGNS_FOR_TEMPLATE_FAILED,
  TEST_TEMPLATE,
  TEMPLATE_AUTO_SAVE_TIMER_START,
  GET_TEMPLATE_TAGS,
  DELETING_TEMPLATES_SUCCEEDED,
  SET_CURRENT_TEMPLATE,
  GETTING_TEMPLATE_SUCCEEDED
} from './actions';
import { actions as UserActions } from '../user';
import { actions as userMessaging } from '../userMessaging';
import { actions as SettingsActions } from '../settings';
import { sagas as SettingsSaga } from '../settings';

/* Helper Sagas */
function* forceGetAllTemplates(action) {
  yield put(getPaginatedTemplates({ force: true }));
  const isInTemplateDesignDialog = yield select(
    store => store.template.isInTemplateDesignDialog
  );
  if (
    action?.type !== 'DELETING_TEMPLATES_SUCCEEDED' &&
    !isInTemplateDesignDialog
  ) {
    const currentTemplate = yield select(store => store.template.current);
    if (currentTemplate.uuid) {
      if(window.self !== window.top) {
        yield put(push(`/unlayerEditor/${currentTemplate.uuid}`));
      } else {
        yield put(push(`/templates/design/${currentTemplate.uuid}`));
      }
    }
  }
}

function* waitForUserReady() {
  const userReady = yield select(store => store.user.ready);
  if (!userReady) {
    yield take(UserActions.LOGIN_READY);
  }
}

function* HandleFailuresSaga() {
  yield takeLatest(
    [
      CREATING_TEMPLATE_FAILED,
      UPDATING_TEMPLATE_FAILED,
      GETTING_TEMPLATE_FAILED,
      GETTING_TEMPLATES_FAILED,
      DELETING_TEMPLATES_FAILED,
      GETTING_ASSOCIATED_CAMPAIGNS_FOR_TEMPLATE_FAILED
    ],
    function*(action) {
      yield put(
        userMessaging.setMessageForFailedAction({
          type: action.type,
          error: action.payload
        })
      );
    }
  );
}

/* APP FLOW - Templates */
function* TemplateGetManySaga() {
  yield takeLatest(GET_TEMPLATES, function*(action) {
    yield call(waitForUserReady);
    yield call(getTemplatesIfNeeded, action);
  });
}
function* PaginatedTemplateGetManySaga() {
  yield takeLatest(GET_PAGINATED_TEMPLATES, function*(action) {
    yield call(waitForUserReady);
    yield call(getPaginatedTemplatesIfNeeded, action);
  });
}
function* MinimalTemplateGetManySaga() {
  yield takeLatest(GET_MINIMAL_TEMPLATES, function*(action) {
    yield call(waitForUserReady);
    yield call(getMinimalTemplatesIfNeeded, action);
  });
}

function* TemplateNameHydrationSaga() {
  yield takeLatest(GET_TEMPLATE_NAMES_BY_UUIDS, function*(action) {
    yield call(waitForUserReady);
    yield call(getTemplateNamesByUuidsIfNeeded, action);
  });
}
function* TagGetSaga() {
  yield takeLatest(GET_TEMPLATE_TAGS, function*(action) {
    yield call(getTagsIfNeeded, action);
  });
}
function* TemplateGetSingleSaga() {
  yield takeLatest(GET_TEMPLATE, function*(action) {
    yield call(waitForUserReady);
    yield call(getTemplateIfNeeded, action);
  });
}

// brutally refresh to template list on any modification - this *could* be made to act just on the in-memory reducer instead.
function* TemplateCreateSaga() {
  yield takeLatest(CREATE_TEMPLATE, createTemplateFromCurrent);
}

function* WarnAndArchiveTemplate() {
  yield takeLatest(WARN_AND_ARCHIVE_TEMPLATE, warnAndArchive);
}

function* TemplateCreateSuccessSaga() {
  yield takeLatest(CREATING_TEMPLATE_SUCCEEDED, forceGetAllTemplates);
}
function* TemplateUpdateSaga() {
  yield takeLatest(UPDATE_TEMPLATE, updateTemplateFromCurrent);
}
function* TemplateUpdateSuccessSaga() {
  yield takeLatest(UPDATING_TEMPLATE_SUCCEEDED, forceGetAllTemplates);
}
function* TemplateDeleteSaga() {
  yield takeLatest(DELETE_TEMPLATES, function*(action) {
    yield call(deleteTemplatesByIdList, action);
    // yield call(forceGetAllTemplates); // force refresh even if the delete is unsuccessful - since we don't know if we only partially failed.
  });
}
function* TemplateDeleteSuccessSaga() {
  yield takeLatest(DELETING_TEMPLATES_SUCCEEDED, forceGetAllTemplates);
}

function* TestTemplateSaga() {
  yield takeLatest(TEST_TEMPLATE, testTemplate);
}

function* AutoSaveTimerSaga() {
  yield takeEvery(TEMPLATE_AUTO_SAVE_TIMER_START, function*(action) {
    yield call(autoSaveTimerStart, action);
  });
}

function* HandleTemplateLanguageChangeSaga() {
  yield call(SettingsSaga.waitForGetAllMetaData);
  const availableLanguages = yield select(store => store.settings.availableLanguages);
  const settingsMetaData = yield select(store => store.settings.settingsMetaData.data);
  yield takeLatest([
    SET_CURRENT_TEMPLATE,
    GETTING_TEMPLATE_SUCCEEDED
  ], function*(action) {
    let languageCode;
    if (action.type === SET_CURRENT_TEMPLATE) {
      languageCode = action.payload?.languageCode;
    } else if (action.type === GETTING_TEMPLATE_SUCCEEDED) {
      languageCode = action.payload?.template?.languageCode;
    }
    // Fall back to settingsMetaData.languageCode if template doesn't have one
    if (!languageCode) {
      languageCode = settingsMetaData?.languageCode;
    }
    if (languageCode && availableLanguages.length > 0) {
      yield put(SettingsActions.updateTemplateLanguagePreference(availableLanguages.filter(language => language.languageCode === languageCode)[0]));
    }
  });
}

export default [
  TemplateGetManySaga,
  PaginatedTemplateGetManySaga,
  MinimalTemplateGetManySaga,
  TagGetSaga,
  TemplateGetSingleSaga,
  TemplateCreateSaga,
  TemplateCreateSuccessSaga,
  TemplateUpdateSaga,
  TemplateUpdateSuccessSaga,
  TemplateDeleteSaga,
  TemplateDeleteSuccessSaga,
  TestTemplateSaga,
  HandleFailuresSaga,
  WarnAndArchiveTemplate,
  AutoSaveTimerSaga,
  HandleTemplateLanguageChangeSaga,
  TemplateNameHydrationSaga
];
